import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {UntypedFormGroup} from '@angular/forms';
import {CreateDisclosureSharedService} from '../../create-disclosure-shared.service';
import {TemplateHeaderService} from './template-header.service';
import {DSTSharedService} from '../../../../../shared/dst-shared.service';

@Component({
    selector: 'dst-template-header',
    templateUrl: './template-header.component.html',
    styleUrls: ['./template-header.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TemplateHeaderComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);


  @Input() parentFormGroup!: UntypedFormGroup;
  @Input() allowOptional!: boolean; // optional currently limited to section 351 - form id 184
  @Input() isBulk!: boolean; // set true for bulk upload and bulk supersede
  @Input() overwriteFormName!: string;
  @Input() extraLine!: string;
  @Input() dropDownOptions!: any[];
  @Input() sameLineText!: string;
  @Output() consolGroupChanged: EventEmitter<DSTConsolGroup>;
  @Output() formOptionChanged: EventEmitter<any>;

  consolGroups: any[] = [];
  selectedConsolGroupItem: any = null;
  selectedOption: any = null;
  placeholderText: string;

  taxYear!: number;
  formName!: string;
  taxReturnDetails: string;

  constructor(private headerService: TemplateHeaderService,
              private dstSharedService: DSTSharedService,
              private sharedService: CreateDisclosureSharedService,
              private elementRef: ElementRef) {
    this.consolGroupChanged = new EventEmitter<DSTConsolGroup>();
    this.formOptionChanged = new EventEmitter<any>();
    this.taxReturnDetails = 'CONSOLIDATED FEDERAL INCOME TAX RETURN';
    this.placeholderText = 'Please specify a valid reporting Entity to determine the Consolidated Group';
  }

  ngOnInit(): void {
    this.taxYear = this.sharedService.getActiveFormState()?.taxYear;
    this.formName = this.sharedService.getActiveFormState()?.formName;
    if (this.overwriteFormName) {
      this.formName = this.overwriteFormName;
    }
    setTimeout(() => this.syncConsolDropdownPlaceholder(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dropDownOptions'] && this.dropDownOptions?.length) {
      const preSelected = this.dropDownOptions.find((o: any) => o.selected);
      if (preSelected) {
        this.selectedOption = preSelected;
      }
    }
    setTimeout(() => this.syncConsolDropdownPlaceholder(), 0);
  }

  loadConsolGroups(leid: string, dcnId: number): void {
    this.headerService.getHeaderDropdown(leid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: DSTConsolGroup[]) => {
      const hasSavedDcn = dcnId != null && dcnId !== undefined && Number(dcnId) > 0;
      data.forEach((d: DSTConsolGroup) => {
        d.value = d.DCNID;
        d.label = d.DCNNAME;
        d.text = d.DCNNAME;
        d.selected = hasSavedDcn && dcnId === d.DCNID;
      });
      this.consolGroups = data;
      let selected = this.consolGroups.find((c: DSTConsolGroup) => c.selected) || null;
      // When there is no saved-DCN match (new disclosure), auto-select the first
      // returned row so the parent template receives consolGroupChanged and its
      // dcnId is populated for the save payload.
      if (!selected && this.consolGroups.length > 0) {
        selected = this.consolGroups[0];
        selected.selected = true;
      }
      this.selectedConsolGroupItem = selected;
      if (selected) {
        this.consolGroupChanged.emit(selected);
      }
      setTimeout(() => this.syncConsolDropdownPlaceholder(), 0);
    }, (e: Error) => {
      this.dstSharedService.showAlert('error', e.message);
    });
  }

  clear(): void {
    this.consolGroups = [];
    this.selectedConsolGroupItem = null;
    setTimeout(() => this.syncConsolDropdownPlaceholder(), 0);
    this.consolGroupChanged.emit(undefined);
  }

  onConsolGroupChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedConsolGroupItem = change;
    setTimeout(() => this.syncConsolDropdownPlaceholder(), 0);
    this.consolGroupChanged.emit(change);
  }

  /** Global ap-dropdown CSS hides the title container; set native placeholder when empty. */
  private syncConsolDropdownPlaceholder(): void {
    const input = this.elementRef.nativeElement.querySelector(
      '.consol-group-dropdown input.ap-field-input'
    ) as HTMLInputElement | null;
    if (!input) {
      return;
    }
  }

  onOptionChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedOption = change;
    this.formOptionChanged.emit(change?.value ?? change?.id);
  }

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }

  onDropdownSelectItem(event: any): void {
  }

}

export interface DSTConsolGroup {
  DCNID: number;
  DCNNAME: string;
  DCNYEARID: number;
  TAXYEAR: number;
  TAXYEARID: number;
  selected?: boolean;
  value?: number;
  text?: string;
  label?: string;
}
